import { useState, useEffect } from "react";
import { Landmark, DoorOpen, DoorClosed, ArrowDownCircle, ArrowUpCircle, Clock, Receipt, History, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCashRegister } from "@/hooks/useCashRegister";
import { useOpenConsumption } from "@/hooks/useOpenConsumption";
import { OpenCashRegisterDialog } from "@/components/cash-register/OpenCashRegisterDialog";
import { CloseCashRegisterDialog } from "@/components/cash-register/CloseCashRegisterDialog";
import { CashMovementDialog } from "@/components/cash-register/CashMovementDialog";
import { CashMovementsList } from "@/components/cash-register/CashMovementsList";
import { PeriodStatusCard } from "@/components/cash-register/PeriodStatusCard";
import { CashRegisterReportDialog } from "@/components/cash-register/CashRegisterReportDialog";
import { formatDistanceToNow, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { NumericKeypadProvider } from "@/components/numeric-keypad/NumericKeypadProvider";
import { NumericKeypadDock } from "@/components/numeric-keypad/NumericKeypadDock";

interface ClosedCashRegister {
  id: string;
  opened_at: string;
  closed_at: string;
  opening_amount: number;
  closing_amount: number;
  expected_amount: number;
  difference: number;
}

export default function CaixaPage() {
  const { activeRestaurant } = useRestaurantContext();
  const {
    currentRegister,
    movements,
    isLoading,
    isOpen,
    openRegister,
    closeRegister,
    addMovement,
    getTotals,
    refetch,
  } = useCashRegister();

  const { openTables, openCommands, isLoading: isLoadingConsumption } = useOpenConsumption();

  const [openDialogType, setOpenDialogType] = useState<"open" | "close" | "withdrawal" | "supply" | null>(null);
  const [closedRegisters, setClosedRegisters] = useState<ClosedCashRegister[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  useEffect(() => {
    const fetchClosedRegisters = async () => {
      if (!activeRestaurant) return;

      const { data } = await supabase
        .from("cash_registers")
        .select("id, opened_at, closed_at, opening_amount, closing_amount, expected_amount, difference")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "closed")
        .order("closed_at", { ascending: false })
        .limit(10);

      setClosedRegisters((data || []) as ClosedCashRegister[]);
    };

    fetchClosedRegisters();
  }, [activeRestaurant, isOpen]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const totals = getTotals();

  // Calculate open consumption totals
  const openTablesCount = openTables.length;
  const openCommandsCount = openCommands.length;
  const totalOpenAmount = [...openTables, ...openCommands].reduce((sum, c) => sum + c.subtotal, 0);

  // Calculate stale (>24h) consumption totals
  const staleTables = openTables.filter(t => t.isStale);
  const staleCommands = openCommands.filter(c => c.isStale);
  const staleTablesCount = staleTables.length;
  const staleCommandsCount = staleCommands.length;
  const staleAmount = [...staleTables, ...staleCommands].reduce((sum, c) => sum + c.subtotal, 0);

  // Numbers for debugging/UX (helps identify “ghost” consumptions)
  const openTableNumbers = openTables.map((t) => t.number);
  const openCommandNumbers = openCommands.map((c) => c.number);
  const staleTableNumbers = staleTables.map((t) => t.number);
  const staleCommandNumbers = staleCommands.map((c) => c.number);

  if (isLoading || isLoadingConsumption) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <NumericKeypadProvider>
      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <Landmark className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Caixa</h1>
            <p className="text-sm text-muted-foreground">Controle de períodos e turnos</p>
          </div>
        </div>
      </div>

      {/* Period Status Card */}
      <PeriodStatusCard hasOpenCashRegister={isOpen} onPeriodOpened={refetch} />


      {/* Cash Register Status Card */}
      <Card className={isOpen ? "border-green-500/50 bg-green-50/50 dark:bg-green-900/10" : "border-muted"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isOpen ? (
                <DoorOpen className="h-6 w-6 text-green-600" />
              ) : (
                <DoorClosed className="h-6 w-6 text-muted-foreground" />
              )}
              <div>
                <CardTitle className="text-lg">
                  {isOpen ? "Caixa Aberto" : "Caixa Fechado"}
                </CardTitle>
                {currentRegister && (
                  <CardDescription className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Aberto há {formatDistanceToNow(new Date(currentRegister.opened_at), { locale: ptBR })}
                  </CardDescription>
                )}
              </div>
            </div>

            {isOpen ? (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setOpenDialogType("supply")}
                >
                  <ArrowDownCircle className="h-4 w-4 text-green-600" />
                  Suprimento
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => setOpenDialogType("withdrawal")}
                >
                  <ArrowUpCircle className="h-4 w-4 text-red-600" />
                  Sangria
                </Button>
                <Button
                  variant="destructive"
                  className="gap-2"
                  onClick={() => setOpenDialogType("close")}
                >
                  <DoorClosed className="h-4 w-4" />
                  Fechar Caixa
                </Button>
              </div>
            ) : (
              <Button className="gap-2" onClick={() => setOpenDialogType("open")}>
                <DoorOpen className="h-4 w-4" />
                Abrir Caixa
              </Button>
            )}
          </div>
        </CardHeader>

        {isOpen && currentRegister && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-background border">
                <p className="text-sm text-muted-foreground">Fundo Inicial</p>
                <p className="text-2xl font-bold">{formatCurrency(totals.openingAmount)}</p>
              </div>
              <div className="p-4 rounded-lg bg-background border">
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <ArrowDownCircle className="h-3 w-3 text-green-600" />
                  Suprimentos
                </p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.supplies)}</p>
              </div>
              <div className="p-4 rounded-lg bg-background border">
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <ArrowUpCircle className="h-3 w-3 text-red-600" />
                  Sangrias
                </p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totals.withdrawals)}</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Movements List */}
      {isOpen && movements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Movimentações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CashMovementsList movements={movements} />
          </CardContent>
        </Card>
      )}

      {/* Closed Registers History */}
      {closedRegisters.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="h-5 w-5" />
              Histórico de Fechamentos
            </CardTitle>
            <CardDescription>
              Últimos 10 fechamentos de caixa
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead className="text-right">Fundo</TableHead>
                  <TableHead className="text-right">Esperado</TableHead>
                  <TableHead className="text-right">Contado</TableHead>
                  <TableHead className="text-right">Diferença</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closedRegisters.map((register) => {
                  const diff = register.difference || 0;
                  return (
                    <TableRow key={register.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {format(new Date(register.closed_at), "dd/MM/yyyy", { locale: ptBR })}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(register.opened_at), "HH:mm")} - {format(new Date(register.closed_at), "HH:mm")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(register.opening_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(register.expected_amount)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(register.closing_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={
                            Math.abs(diff) < 0.01
                              ? "bg-green-50 text-green-700 border-green-200"
                              : diff > 0
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-red-50 text-red-700 border-red-200"
                          }
                        >
                          {Math.abs(diff) < 0.01
                            ? "OK"
                            : diff > 0
                            ? `+${formatCurrency(diff)}`
                            : formatCurrency(diff)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedReportId(register.id)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <OpenCashRegisterDialog
        open={openDialogType === "open"}
        onOpenChange={(open) => !open && setOpenDialogType(null)}
        onConfirm={async (amount) => {
          await openRegister(amount);
          setOpenDialogType(null);
        }}
      />

      <CloseCashRegisterDialog
        open={openDialogType === "close"}
        onOpenChange={(open) => !open && setOpenDialogType(null)}
        onConfirm={async (amount, notes) => {
          await closeRegister(amount, notes);
          setOpenDialogType(null);
        }}
        totals={totals}
        cashRegisterId={currentRegister?.id}
        openTablesCount={openTablesCount}
        openCommandsCount={openCommandsCount}
        totalOpenAmount={totalOpenAmount}
        openTableNumbers={openTableNumbers}
        openCommandNumbers={openCommandNumbers}
        staleTablesCount={staleTablesCount}
        staleCommandsCount={staleCommandsCount}
        staleAmount={staleAmount}
        staleTableNumbers={staleTableNumbers}
        staleCommandNumbers={staleCommandNumbers}
      />

      <CashMovementDialog
        open={openDialogType === "withdrawal" || openDialogType === "supply"}
        onOpenChange={(open) => !open && setOpenDialogType(null)}
        type={openDialogType === "withdrawal" ? "withdrawal" : "supply"}
        onConfirm={async (amount, reason, expense) => {
          if (openDialogType === "withdrawal" || openDialogType === "supply") {
            await addMovement(openDialogType, amount, reason, expense);
            setOpenDialogType(null);
          }
        }}
      />

          <CashRegisterReportDialog
            open={!!selectedReportId}
            onOpenChange={(open) => !open && setSelectedReportId(null)}
            cashRegisterId={selectedReportId}
          />
        </div>

        {/* Keypad Dock (fixo no layout) */}
        <div className="hidden lg:block">
          <div className="sticky top-6">
            <NumericKeypadDock />
          </div>
        </div>
      </div>
    </NumericKeypadProvider>
  );
}
